import { useMemo } from 'react';
import apiClient from 'src/api/axiosClient';
import {
  InboxContact,
  InboxContactChat,
  InboxContactChatLinks,
} from 'src/constants/types';
import {
  AddInboxMessageParams,
  GetInboxContactChatParams,
  GetInboxContactsParams,
  InboxMarkChatAsReadParams,
  InboxMarkMessageAsUnreadParams,
  UpdateInboxMessageParams,
} from '../types';
import { createInFlightGet, stableParamsKey } from './inFlightGet';

// Scopes gate which callers of getContacts/getContactChat can supersede each
// other. Different features sharing this hook (inbox list, inbox new-chat
// search, campaign builder's phone typeahead) must NOT abort each other's
// requests — only calls that pass the same scope compete "latest wins".
export const INBOX_CONTACTS_LIST_SCOPE = 'inbox-contacts-list';
export const INBOX_CONTACTS_SEARCH_SCOPE = 'inbox-contacts-search';
export const INBOX_CONTACT_CHAT_SCOPE = 'inbox-contact-chat';

const getContactsInFlight = createInFlightGet<
  GetInboxContactsParams,
  { items: InboxContact[]; count: number }
>({
  path: '/message-history/contacts',
  keyOf: params => stableParamsKey({ ...params }),
  toMeta: params => ({ offset: params.offset }),
  // First-page fetches supersede other first-page fetches (filter/poll/refresh).
  // Pagination (offset > 0) is left alone so it can run beside a poll.
  shouldSupersede: (incoming, inFlightMeta) =>
    incoming.offset === 0 && inFlightMeta.offset === 0,
});

const getContactChatInFlight = createInFlightGet<GetInboxContactChatParams, InboxContactChat>({
  path: '/message-history/contact-chat',
  keyOf: params => stableParamsKey({ ...params }),
  toMeta: params => ({ contactId: params.contactId }),
  // Switching contacts aborts the previous conversation's in-flight GETs.
  shouldSupersede: (incoming, inFlightMeta) => inFlightMeta.contactId !== incoming.contactId,
});

export function useInboxApi() {
  return useMemo(
    () => ({
      getContacts: getContactsInFlight,

      getContactChat: getContactChatInFlight,

      getContactChatLinks: (params: GetInboxContactChatParams) => {
        return apiClient.get<InboxContactChatLinks>(`/message-history/contact-chat-links`, {
          params,
        });
      },

      markChatAsRead: (params: InboxMarkChatAsReadParams, companyId: string) => {
        return apiClient.put(`/message-history/mark-read`, params, { params: { companyId } });
      },

      markMessageAsUnread: (params: InboxMarkMessageAsUnreadParams, companyId: string) => {
        return apiClient.put(`/message-history/mark-unread`, params, { params: { companyId } });
      },

      addMessage: (params: AddInboxMessageParams, companyId: string) => {
        return apiClient.post(`/message-history`, params, { params: { companyId } });
      },

      deleteMessage: (messageId: string, companyId: string) => {
        return apiClient.delete(`/message-history/${messageId}`, { params: { companyId } });
      },

      updateMessage: (messageId: string, params: UpdateInboxMessageParams, companyId: string) => {
        return apiClient.put(`/message-history/${messageId}`, params, { params: { companyId } });
      },
    }),
    [],
  );
}
